/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/components/Layout/Sidebar.tsx
 * DESCRIPTION: Main navigation sidebar component for the VeriMark
 *              platform. Provides persistent navigation across all
 *              authenticated pages with organization context, user
 *              profile access, and admin submenu.
 *
 * FEATURES:
 * - Dynamic organization display and switching context
 * - Navigation items with active state highlighting
 * - User profile section with sign-out functionality
 * - Settings submenu with admin-only pages (Clerk role-gated)
 * - Responsive design optimized for desktop layout
 * - Icon-based navigation using Lucide React icons
 * - Dark mode support
 * - DESKTOP: Always visible (250px expanded by default), collapsible to 72px
 * - MOBILE: Overlay sidebar with hamburger toggle
 *
 * ADMIN ACCESS:
 * - Admin menu items only visible if user.publicMetadata.role === 'admin'
 * - No lock icon; non-admins simply don't see admin items
 * - Authorization checked via useAdminAuth() hook
 *
 * NAVIGATION STRUCTURE:
 * 1. Dashboard - Overview and quick stats
 * 2. Telemetry - Real-time analytics
 * 3. Analytics - Historical trends
 * 4. Analyze File - Upload and analyze media files
 * 5. Library - Media library and identity management
 * 6. Identities - File identity details
 * 7. AI Providers - AI provider configuration
 * 8. Organizations - Manage multiple organization contexts
 * 9. Billing & Plans - Subscription management
 * 10. Audit Logs - API keys, webhooks, and activity tracking
 * 11. Settings > Admin (Analytics, AI Providers, User Management) [ADMIN ONLY]
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 2.3.0
 * @created 2025-01-15
 * @updated 2026-04-13 - Added desktop autohide (collapse/expand on hover)
 *                       + pin toggle, preserved mobile behavior
 * @updated 2026-04-14 - Fixed desktop visibility: sidebar always visible
 *                       on desktop (250px default), removed -translate-x-full
 * @updated 2026-04-14 - Fixed menu sync with App.tsx routes
 * @updated 2026-09-23 - Admin menu items now gated by Clerk metadata role
 *                       (useAdminAuth), removed lock icon for non-admins
 * ─────────────────────────────────────────────────────────────────────
 */

import { NavLink, useNavigate } from 'react-router-dom';
import { useUser, useOrganization, useClerk } from '@clerk/react';
import {
  LayoutDashboard,
  Upload,
  Building2,
  CreditCard,
  FileText,
  TrendingUp,
  LogOut,
  ChevronDown,
  FolderOpen,
  Cpu,
  Settings,
  Shield,
  IdCard,
  Menu,
  X,
  Activity,
  Pin,
  PinOff,
} from 'lucide-react';
import { useState } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

interface NavItem {
  to: string;
  icon: React.ElementType;
  label: string;
}

interface AdminMenuItem {
  to: string;
  icon: React.ElementType;
  label: string;
}

const Sidebar: React.FC = () => {
  const { user } = useUser();
  const { organization } = useOrganization();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const { isAdminAuthenticated } = useAdminAuth();
  
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  
  // Desktop default to COLLAPSED (retracted by default)
  const [isDesktopCollapsed, setIsDesktopCollapsed] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  
  // Desktop is expanded if NOT collapsed OR hovered
  const isDesktopExpanded = !isDesktopCollapsed || isHovered;

  // Menu items with Telemetry in correct position
  const navItems: NavItem[] = [
    { to: '/analyze', icon: Upload, label: 'Verify Media' },
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/library', icon: FolderOpen, label: 'Library' },
    //{ to: '/intel', icon: Activity, label: 'Intel Telemetry' },         // this is just for testing but has been moved to admin. 
  ];

  // Admin menu items - only rendered if user is admin
  const adminMenuItems: AdminMenuItem[] = [
    { to: '/admin/analytics', icon: TrendingUp, label: 'Analytics' },
    { to: '/admin/intel', icon: Activity, label: 'Intel Telemetry' },
    { to: '/admin/telemetry', icon: Activity, label: 'Live Intel Telemetry' },
    { to: '/admin/mapintel', icon: TrendingUp, label: 'Standalone Map' },
    { to: '/admin/ai-providers', icon: Cpu, label: 'AI Settings' },
    { to: '/admin/users', icon: Shield, label: 'User Management' },
    { to: '/admin/audit', icon: FileText, label: 'Audit Logs' },
  ];

  const handleSignOut = () => {
    signOut(() => {
      window.location.href = '/login';
    });
  };

  // Label visibility: shown when expanded OR hovered
  const labelTextClass = `transition-all duration-300 ${
    isDesktopExpanded ? 'opacity-100 max-w-full' : 'opacity-0 max-w-0'
  } whitespace-nowrap overflow-hidden`;

  return (
    <>
      {/* Mobile Hamburger Toggle */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="lg:hidden fixed top-3 left-4 z-50 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700"
      >
        {isMobileOpen ? (
          <X className="w-5 h-5 text-gray-900 dark:text-white" />
        ) : (
          <Menu className="w-5 h-5 text-gray-900 dark:text-white" />
        )}
      </button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`
          /* Base styles - ALWAYS applied */
          bg-white dark:bg-slate-900
          text-gray-900 dark:text-white
          flex flex-col
          fixed h-full z-50
          border-r border-gray-200 dark:border-slate-800
          /* Transition - ALWAYS applied */
          transition-all duration-300 ease-in-out
          /* Mobile default: HIDDEN (off-screen left) */
          -translate-x-full
          /* Mobile when open: OVERRIDES the -translate-x-full above */
          ${isMobileOpen ? 'translate-x-0' : ''}
          /* FIXED: Desktop ALWAYS VISIBLE (no translate), width changes */
          lg:translate-x-0
          lg:left-0
          ${isDesktopExpanded ? 'lg:w-[250px]' : 'lg:w-[72px]'}
        `}
      >
        {/* Logo + Pin Toggle */}
        <div className="p-4 border-b border-gray-200 dark:border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/logo.svg"
              alt="Originify"
              className="h-8 w-auto shrink-0"
            />
            <span className={`text-xl font-semibold ${labelTextClass}`}>Originify</span>
          </div>
          {/* Desktop Pin Toggle */}
          <button
            onClick={() => setIsDesktopCollapsed(!isDesktopCollapsed)}
            className="hidden lg:flex p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
            title={isDesktopCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isDesktopCollapsed ? (
              <Pin className="w-4 h-4 text-gray-500 dark:text-slate-400" />
            ) : (
              <PinOff className="w-4 h-4 text-blue-500" />
            )}
          </button>
        </div>

        {/* Organization Context */}
        {organization && (
          <div className={`px-2 py-3 border-b border-gray-200 dark:border-slate-800 ${
            !isDesktopExpanded ? 'lg:hidden' : ''
          }`}>
            <NavLink
              to="/organizations"
              onClick={() => setIsMobileOpen(false)}
              className="flex items-center justify-between px-3 py-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center shrink-0">
                  <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{organization.name}</p>
                  <p className="text-xs text-gray-600 dark:text-slate-400">Organization</p>
                </div>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-600 dark:text-slate-400 shrink-0" />
            </NavLink>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto sidebar-scroll">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setIsMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white'
                    : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800'
                }`
              }
            >
              <item.icon className="w-5 h-5 shrink-0" />
              <span className={`text-sm font-medium ${labelTextClass}`}>{item.label}</span>
            </NavLink>
          ))}

          {/* Settings with Admin Submenu - Only show if admin */}
          {isAdminAuthenticated && (
            <div>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  setIsSettingsOpen(!isSettingsOpen);
                }}
                className={`flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors w-full ${
                  isSettingsOpen
                    ? 'bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white'
                    : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Settings className="w-5 h-5 shrink-0" />
                  <span className={`text-sm font-medium ${labelTextClass}`}>Settings</span>
                </div>
                <ChevronDown
                  className={`w-4 h-4 transition-transform shrink-0 ${
                    isSettingsOpen ? 'rotate-180' : ''
                  } ${!isDesktopExpanded ? 'lg:hidden' : ''}`}
                />
              </button>

              {/* Admin Submenu */}
              {isSettingsOpen && (
                <div className={`ml-4 mt-1 space-y-1 ${!isDesktopExpanded ? 'lg:hidden' : ''}`}>
                  {adminMenuItems.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setIsMobileOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm ${
                          isActive
                            ? 'bg-gray-200 dark:bg-slate-700 text-gray-900 dark:text-white'
                            : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700'
                        }`
                      }
                    >
                      <item.icon className="w-4 h-4 shrink-0" />
                      <span className="flex-1 truncate">{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* User Profile */}
        <div className={`p-3 border-t border-gray-200 dark:border-slate-800 ${
          !isDesktopExpanded ? 'lg:hidden' : ''
        }`}>
          <NavLink
            to="/profile"
            onClick={() => setIsMobileOpen(false)}
            className="flex items-center gap-3 mb-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            {user?.imageUrl ? (
              <img
                src={user.imageUrl}
                alt={user.firstName || 'User'}
                className="w-8 h-8 rounded-full object-cover shrink-0"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium shrink-0">
                {user?.firstName?.charAt(0) || user?.emailAddresses[0]?.emailAddress?.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {user?.firstName || user?.emailAddresses[0]?.emailAddress}
              </p>
              <p className="text-xs text-gray-600 dark:text-slate-400 truncate">
                {user?.emailAddresses[0]?.emailAddress}
              </p>
            </div>
          </NavLink>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5 shrink-0" />
            <span className="text-sm font-medium">Sign Out</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;